/*
 | ==================================================
 | This File is for front end constants for memberhub
 | ==================================================
 */
import { AU_COUNTRY_CODE } from '#shared/phone'
const VERTICAL_IDS = {
  HEALTH_INSURANCE: 1,
  HOME_LOANS: 2,
  LIFE_INSURANCE: 3,
  ENERGY: 4,
  CAR_LOANS: 5,
  PERSONAL_LOANS: 6,
  CAR_INSURANCE: 7,
  CREDIT_CARDS: 8,
  GENERIC: 9
}

const HEALTH_ENERGY_BUNDLE_GIFT_CARD_UTM_TERM = 'EOFY-Health-Energy-Bundle-250-gift-card'
export default {
  /*
     * API Status codes
     */
  API_SUCCESS: 200,
  API_CREATED: 201,
  API_BAD_REQUEST: 400,
  API_TOKEN_EXPIRED: 401,
  API_FORBIDDEN: 403,
  API_NOT_FOUND: 404,
  API_CONFLICT: 409,
  API_VALIDATION_ERROR: 422,
  API_MAINTENANCE: 503,
  API_PERMANENT_REDIRECT: 308,
  /*
   * Max age of a cached vault detail. Its files[].file_url are presigned for 15 minutes
   * (memberhub PRESIGNED_URL_TTL_MINUTES, CLUB-899), so refetch well before that.
   */
  VAULT_PRODUCT_MAX_AGE_MS: 10 * 60 * 1000,
  /*
     * Social auth constants
     */
  GOOGLE_AUTH: 1,
  FACEBOOK_AUTH: 2,
  LINKEDIN_AUTH: 3,
  APPLE_AUTH: 4,
  /*
     * Account settings sections
     */
  PERSONAL: 1,
  COMMUNICATION: 2,
  PASSWORD: 3,
  ENQUIRY: 4,
  /*
     * Member hub localstorage keys to be preserved
     */
  MEMBERHUB_KEYS_TO_BE_PRESERVED: ['auth_user', 'token'],
  /*
     * Available vertical's slugs
     */
  VERTICAL_SLUGS: {
    HEALTH_INSURANCE: 'hic',
    HEALTH_INSURANCE_OVERSEAS: 'hicovs',
    LIFE_INSURANCE: 'lic',
    ENERGY: 'cc-eng',
    ENERGY_LEGACY: 'eng',
    HOME_LOANS: 'hla',
    PERSONAL_LOANS: 'pla',
    CAR_INSURANCE: 'carinsurance',
    CAR_LOANS: 'cla',
    CREDIT_CARDS: 'ccc'
  },

  /*
    SessionStorage keys to be preserved
    */
  SESSIONSTORAGE_KEYS_TO_BE_PRESERVED: ['utms'],

  /*
    SessionStorage key set when a new Apple account signs up with a private-relay email.
    While it is present the dashboard shows the one-time "Link your records" prompt.
    */
  APPLE_RELAY_LINK_PENDING_KEY: 'apple_relay_link_pending',
  GLOBAL_FORMS: {
    REGISTER: 1,
    LOGIN: 2
  },
  /*
     * Vertical ID(s)
     */
  VERTICAL_ID: {
    HEALTH_INSURANCE: VERTICAL_IDS.HEALTH_INSURANCE,
    HOME_LOANS: VERTICAL_IDS.HOME_LOANS,
    LIFE_INSURANCE: VERTICAL_IDS.LIFE_INSURANCE,
    ENERGY: VERTICAL_IDS.ENERGY,
    CAR_LOANS: VERTICAL_IDS.CAR_LOANS,
    PERSONAL_LOANS: VERTICAL_IDS.PERSONAL_LOANS,
    CAR_INSURANCE: VERTICAL_IDS.CAR_INSURANCE,
    CREDIT_CARDS: VERTICAL_IDS.CREDIT_CARDS,
    GENERIC: VERTICAL_IDS.GENERIC
  },

  VERTICAL_LABELS:{
    [VERTICAL_IDS.HEALTH_INSURANCE]: 'Health Insurance',
    [VERTICAL_IDS.HOME_LOANS]: 'Home Loans',
    [VERTICAL_IDS.LIFE_INSURANCE]: 'Life insurance',
    [VERTICAL_IDS.ENERGY]: 'Energy',
    [VERTICAL_IDS.CAR_LOANS]: 'Car Loans',
    [VERTICAL_IDS.PERSONAL_LOANS]: 'Personal Loans',
    [VERTICAL_IDS.CAR_INSURANCE]: 'Car Insurance',
    [VERTICAL_IDS.CREDIT_CARDS]: 'Credit Cards',
    [VERTICAL_IDS.GENERIC]: 'Generic'
  },
  DASHBOARD_CROSS_SELL_ITEMS: [
    {
      id: 'dashboard-cross-sell-energy',
      verticalId: VERTICAL_IDS.ENERGY,
      iconKey: 'energy',
      pillLabel: 'ENERGY',
      pillThemeToken: 'base-rose-rose',
      title: 'Save up to $332/year!¹',
      description: 'Average customers save $225 on electricity, $66 on gas',
      ctaLabel: 'Compare & Save',
      actionType: 'route',
      actionKey: 'energy',
      openInNewTab: false,
      actionPayload: {
        path: '/dashboard/offers/energy/gift-card/',
        query: {
          utm_term: 'giftcard_50'
        }
      },
      themeToken: 'semantic-surface-container-lowest',
      ribbonImagePath: '/images/clubplus/ribbon-gift-card.svg',
      imagePath: 'https://asset.compareclub.com.au/clubplus/assets/images/energy-x-sell-card.svg',
      imageAlt: 'Energy savings'
    },
    {
      id: 'dashboard-cross-sell-health-insurance',
      verticalId: VERTICAL_IDS.HEALTH_INSURANCE,
      iconKey: 'healthInsurance',
      pillLabel: 'HEALTH INSURANCE',
      pillThemeToken: 'semantic-vertical-insurance-low',
      title: 'Save up to $401/year²',
      description: 'Join families already saving on their health cover.',
      ctaLabel: 'Compare & Save',
      actionType: 'route',
      actionKey: 'health_insurance',
      openInNewTab: false,
      actionPayload: {
        path: '/dashboard/quick-compare/health',
        query: {
          source: 'clubplus-dashboard'
        }
      },
      themeToken: 'semantic-surface-container-lowest',
      imagePath: 'https://asset.compareclub.com.au/clubplus/assets/images/health-insurance-x-sell-card.svg',
      imageAlt: 'Health insurance savings'
    }
  ],
  /*
   * Variants for the dashboard gift-card cross-sell module
   * (app/components/v2/organism/Dashboard/CrossSell/giftCard.vue).
   *
   * The component selects ONE variant at runtime based on the user's vault
   * purchases:
   *   - default → user has neither Health nor Energy. Routes to energy QC.
   *   - energy  → user has Health but not Energy; cross-sells Energy.
   *   - claim   → user has Energy; surfaces the reward-claim path.
   *
   * Shape per variant:
   *   id                      string  — stable for keying / tracking.
   *   verticalId?             number  — VERTICAL_IDS value. Only on cross-sell
   *                                     variants. Drives pill icon + text colour.
   *   iconKey?                string  — looked up in the component's
   *                                     verticalIconByKey map. Pair with
   *                                     verticalId.
   *   pillLabel               string  — pill text (vertical name or "Club+").
   *   pillThemeToken          string  — mapped to `bg-{token}` Tailwind class
   *                                     by the component.
   *   title                   string  — headline copy.
   *   description             string  — body copy.
   *   ctaLabel                string  — button text.
   *   actionType              'route' | 'external' — handled by
   *                                     handleGiftCardAction in the component.
   *   actionKey               string  — analytics key.
   *   openInNewTab            boolean — defaults true when undefined.
   *   actionPayload           { path, query } for 'route', { url } for 'external'.
   *   trackingAdditionalParams? object — extra params forwarded to V2AtomsButton
   *                                      click tracking.
   *   imagePath               string  — right-hand illustration.
   *   imageAlt                string
   *
   * Editing rules:
   *   - Adding a new variant requires updating the component's variantKey
   *     computed and (if it has a vertical icon) verticalIconByKey.
   */
  DASHBOARD_CROSS_SELL_GIFT_CARD_VARIANTS: {
    default: {
      id: 'dashboard-cross-sell-gift-card-default',
      pillLabel: 'Club+',
      pillThemeToken: 'semantic-primary-default',
      title: 'Get up to $250 Gift Card*',
      description: 'Switch Health and Energy through us and get rewarded.',
      ctaLabel: 'Compare & Save',
      actionType: 'route',
      actionKey: 'gift_card_default',
      openInNewTab: false,
      actionPayload: {
        path: '/dashboard/quick-compare/energy/',
        query: {
          utm_term: HEALTH_ENERGY_BUNDLE_GIFT_CARD_UTM_TERM
        }
      },
      imagePath: '/images/clubplus/gift-card.svg',
      imageAlt: '$250 gift card offer'
    },
    energy: {
      id: 'dashboard-cross-sell-gift-card-energy',
      verticalId: VERTICAL_IDS.ENERGY,
      iconKey: 'energy',
      pillLabel: 'ENERGY',
      pillThemeToken: 'base-rose-rose',
      title: 'One step away from $250*',
      description: 'Add Energy and keep your Health cover active for 30 days.',
      ctaLabel: 'Compare & Save',
      actionType: 'route',
      actionKey: 'gift_card_energy',
      openInNewTab: false,
      actionPayload: {
        path: '/dashboard/quick-compare/energy/',
        query: {
          utm_term: HEALTH_ENERGY_BUNDLE_GIFT_CARD_UTM_TERM
        }
      },
      imagePath: '/images/clubplus/gift-card.svg',
      imageAlt: '$250 gift card offer'
    },
    claim: {
      id: 'dashboard-cross-sell-gift-card-claim',
      pillLabel: 'Club+',
      pillThemeToken: 'semantic-primary-default',
      title: 'Your $250 reward is getting ready',
      description: 'See where you\'re at and when you can claim it.',
      ctaLabel: 'Check Now',
      actionType: 'route',
      actionKey: 'gift_card_claim',
      openInNewTab: false,
      actionPayload: {
        path: '/dashboard/rewards/'
      },
      imagePath: '/images/clubplus/gift-card.svg',
      imageAlt: '$250 gift card offer'
    }
  },
  HEALTH_ENERGY_BUNDLE_GIFT_CARD_DISCLAIMERS: {
    default:'https://get.healthinsurancecomparison.com.au/eofy-2026-health-energy-bundle'
  },
  HEALTH_ENERGY_BUNDLE_GIFT_CARD_UTM_TERM,
  DASHBOARD_CROSS_SELL_DYNAMIC: {
    energyByState: {
      NSW: {
        title: 'Save up to $444/year¹',
        description: 'NSW customer savings compared to the reference price',
        savingsAmount: 444,
        savingsMarker: '1'
      },
      QLD: {
        title: 'Save up to $365/year¹',
        description: 'QLD customer savings compared to the reference price',
        savingsAmount: 365,
        savingsMarker: '1'
      },
      VIC: {
        title: 'Save up to $338/year¹',
        description: 'VIC customer savings compared to the reference price',
        savingsAmount: 338,
        savingsMarker: '1'
      },
      SA: {
        title: 'Save up to $392/year¹',
        description: 'SA customer savings compared to the reference price',
        savingsAmount: 392,
        savingsMarker: '1'
      }
    },
    /*
     * Last-resort energy figure, used when neither a postcode-level deal nor a supported
     * state is known. Mirrors the generic branch of
     * CoreActionsRepository::resolveEnergySavings() in memberhub-backend
     * (app/Repositories/CoreActions/CoreActionsRepository.php:238-243).
     */
    energyGeneric: {
      title: 'Save up to $332/year¹',
      description: 'Average customers save $225 on electricity, $66 on gas',
      savingsAmount: 332,
      savingsMarker: '1'
    },
    healthByHousehold: {
      Single: {
        title: 'Save up to $211/year²',
        description: 'Join singles already saving on their health cover.',
        savingsAmount: 211,
        savingsMarker: '2'
      },
      Couple: {
        title: 'Save up to $115/year²',
        description: 'Join couples already saving on their health cover.',
        savingsAmount: 115,
        savingsMarker: '2'
      },
      Family: {
        title: 'Save up to $401/year²',
        description: 'Join families already saving on their health cover.',
        savingsAmount: 401,
        savingsMarker: '2'
      },
      'Single Parent': {
        title: 'Save up to $286/year²',
        description: 'Join single parents already saving on their health cover.',
        savingsAmount: 286,
        savingsMarker: '2'
      }
    }
  },
  DASHBOARD_CROSS_SELL_DISCLAIMERS: {
    both: [
      {
        marker: '1',
        text: 'Based on 31st Mar 2026 compared to the energy reference price in your state. Compare Club doesn\'t compare all plans in the market, and not all plans are available to every customer. Your savings may vary depending on your meter and tariff.'
      },
      {
        marker: '2',
        text: 'Based on internal Compare Club data showing average savings for Family policies sold from 1 Jan - 31 Dec 2025'
      }
    ],
    healthInsurance: [
      {
        marker: '2',
        text: 'Based on internal Compare Club data showing average savings for Family policies sold from 1 Jan - 31 Dec 2025'
      }
    ],
    energy: [
      {
        marker: '1',
        text: 'Based on 31st Mar 2026 compared to the energy reference price in your state. Compare Club doesn\'t compare all plans in the market, and not all plans are available to every customer. Your savings may vary depending on your meter and tariff.'
      }
    ]
  },
  DASHBOARD_CROSS_SELL_DYNAMIC_DISCLAIMERS: {
    energy: {
      supportedStates: ['NSW', 'QLD', 'VIC', 'SA'],
      disclaimer: {
        marker: '1',
        text: 'Based on 9th April 2026 Compare Club average customer savings. Compare Club doesn\'t compare all plans in the market, and not all plans are available to every customer. Your savings may vary depending on your meter and tariff.'
      }
    },
    healthByHousehold: {
      Single: {
        marker: '2',
        text: 'Based on internal Compare Club data showing average savings for Single policies sold from 1 Jan - 31 Dec 2025'
      },
      Couple: {
        marker: '2',
        text: 'Based on internal Compare Club data showing average savings for Couple policies sold from 1 Jan - 31 Dec 2025'
      },
      'Single Parent': {
        marker: '2',
        text: 'Based on internal Compare Club data showing average savings for Single parent policies sold from 1 Jan - 31 Dec 2025'
      }
    }
  },
  DASHBOARD_ENERGY_GIFT_CARD_PAGE: {
    title: 'Get a $50 Gift Card',
    description: 'Switch your energy plan through Compare Club and we\'ll send you a $50 prepaid Visa gift card, use it anywhere Visa is accepted, in store or online.',
    backPath: '/dashboard/',
    imagePath: '/images/clubplus/gift-card.svg',
    imageAlt: '$50 gift card offer',
    legalContent: [
      {
        text: 'Terms & Conditions apply.',
        bold: true
      },
      {
        text: ' $50 prepaid Visa gift card offer available to new customers who purchase an energy plan through Compare Club. Gift card will be issued within 90 days of policy activation. One gift card per household. Offer subject to change or withdrawal without notice. Compare Club reserves the right to cancel the offer if fraudulent activity is detected. This offer cannot be combined with any other promotion.',
        bold: false
      }
    ],
    cta: {
      label: 'Continue',
      path: '/dashboard/quick-compare/energy/'
    }
  },
  PERKS_CATEGORY_SLUGS: {
    1: 'healthinsurance',
    2: 'homeloans',
    3: 'lifeinsurance',
    4: 'energy',
    5: 'carloans',
    6: 'personalloans',
    7: 'carinsurance',
    8: 'creditcards',
    9: 'generic'
  },
  /*
     * Deal alert(s) status label(s)
     */
  DEAL_ALERT_STATUS_LABEL: {
    0: null,
    1: 'ALERT SET',
    2: 'ALERT FOUND',
    3: 'COVERED'
  },
  /*
     * Support email for memberhub
     */
  CONTACT_EMAIL: 'myaccount@compareclub.com.au',
  /*
     * One tap prompt will be enabled in these routes
     */
  ONE_TAP_ENABLED_ROUTES: [
    '/',
    '/login/',
    '/register/',
    '/forgot-password/',
    '/health-insurance/',
    '/life-insurance/',
    '/car-insurance/',
    '/home-loans/',
    '/car-loans/',
    '/personal-loans/',
    '/energy/',
    '/expert-analysis/',
    '/life-insurance/over-50s-review/',
    '/health-insurance/dental-cover/',
    '/credit-cards/frequent-flyer-cards/',
    '/health-insurance/seniors/',
    '/health-insurance/singles/',
    '/energy/top-10-companies/',
    '/about-us/',
    '/health-insurance/ambulance-cover/',
    '/health-insurance/companies/',
    '/credit-cards/balance-transfer-cards/',
    '/careers/',
    '/life-insurance/funeral-cover/',
    '/health-insurance/couples/',
    '/life-insurance/over-50s/',
    '/credit-cards/'
  ],

  /*
   * Routes that should be disabled on mobile devices
   */
  MOBILE_DISABLED_ROUTES: [
    '/',
    '/health-insurance/',
    '/life-insurance/',
    '/car-insurance/',
    '/home-loans/',
    '/car-loans/',
    '/personal-loans/',
    '/energy/',
    '/credit-cards/'
  ],
  /*
     * One tap prompt will be enabled in pages which contains these route parts
     */
  ONE_TAP_ENABLED_ROUTE_SET: [
    '/expert-analysis/'
  ],

  /*
     * Auth related routes
     */
  AUTH_ROUTES: [
    '/login/',
    '/register/',
    '/forgot-password/',
    '/reset-password/',
    '/dashboard/login/',
    '/dashboard/register/',
    '/dashboard/forgot-password/',
    '/dashboard/reset-password/',
    '/guest/set-password/'
  ],
  AUTH_ROUTES_SET: {
    LOGIN: '/login/',
    PWA_LOGIN: '/dashboard/login/',
    REGISTER: '/register/',
    PWA_REGISTER: '/dashboard/register/',
    FORGOT_PASSWORD: '/forgot-password/',
    PWA_FORGOT_PASSWORD: '/dashboard/forgot-password/',
    RESET_PASSWORD: '/reset-password/',
    PWA_RESET_PASSWORD: '/dashboard/reset-password/',
    GUEST_WELCOME_BACK:'/guest/welcome-back/',
    PWA_GUEST_WELCOME_BACK:'/dashboard/guest/welcome-back/',
    LINK_EXPIRED: '/link-expired/',
    PWA_LINK_EXPIRED: '/dashboard/link-expired/'
  },

  /*
     * Password meter constants
     */
  PASSWORD_METER: {
    POOR: 1,
    WEAK: 2,
    GOOD: 3,
    STRONG: 4
  },
  PASSWORD_METER_REQUIRED_KEYS: {
    MIN_LENGTH: 1,
    UPPERCASE: 2,
    LOWERCASE: 3,
    SPECIAL_CHAR: 4,
    NUMBER: 5
  },

  /*
     * Gift card constants
     */
  GIFT_CARD_STATUS: {
    PENDING: 1,
    AVAILABLE: 2,
    ACTIVATED: 3,
    ARCHIVED: 4,
    INELIGIBLE: 5,
    EXPIRED: 6
  },
  ENERGY_PRODUCT_TYPE: {
    1: 'ELECTRICITY',
    2: 'GAS'
  },
  ENERGY_PRODUCT_TYPE_FLAG: {
    ELECTRICITY: 1,
    GAS: 2
  },
  GENERIC_PRODUCT_TYPE: {
    1: 'BROADBAND',
    2: 'SIM'
  },
  GENERIC_PRODUCT_TYPE_FLAG: {
    BROADBAND: 1,
    SIM: 2
  },
  /*
     * Vault funnel support constants
     */
  PAYMENT_FREQUENCY: {
    HEALTH_INSURANCE: [
      'Weekly',
      'Fortnightly',
      'Monthly',
      'Quarterly',
      'Half-yearly',
      'Annually'
    ],
    ENERGY: [
      'Monthly',
      'Bi-Monthly',
      'Quarterly',
      'Annually'
    ],
    LIFE: [
      'Fortnightly',
      'Monthly',
      'Quarterly',
      'Half-yearly',
      'Annually'
    ],
    GENERIC: [
      'Weekly',
      'Fortnightly',
      'Monthly',
      'Quarterly',
      'Annually'
    ],
    HOME_LOAN: [
      'Weekly',
      'Fortnightly',
      'Monthly'
    ]
  },
  PAYMENT_BILL_AMOUNTS: {
    ENERGY: {
      Monthly: [
        '$0-$99',
        '$100-$149',
        '$150-$199',
        '$200-$249',
        '$250-$299',
        '$300+'
      ],
      'Bi-Monthly': [
        '$0-$199',
        '$200-$299',
        '$300-$399',
        '$400-$499',
        '$500-$599',
        '$600+'
      ],
      Quarterly: [
        '$0-$299',
        '$300-$449',
        '$450-$599',
        '$600-$749',
        '$750-$899',
        '$900+'
      ],
      Annually: [
        '$0-$1,199',
        '$1,200-$1,799',
        '$1,800-$2,399',
        '$2,400-$2,999',
        '$3,000-$3,599',
        '$3,600+'
      ]
    }
  },
  COVER_TYPES: {
    HEALTH_INSURANCE: [
      {
        label: 'Hospitals + Extras',
        value: 'Combined'
      },
      {
        label: 'Hospital only',
        value: 'Hospital'
      },
      {
        label: 'Extras only',
        value: 'GeneralHealth'
      }
    ]
  },
  /**
   * Normalised health policy type values derived in getters.
   * Used as the health_policy_type key on getProduct / getProducts.
   *  COMBINED  – hospital + extras (cover_type 'Combined' or extra_cover_exists)
   *  HOSPITAL  – hospital only
   *  EXTRAS    – extras / general health only
   */
  HEALTH_POLICY_TYPE: {
    COMBINED: 'combined',
    HOSPITAL: 'hospital',
    EXTRAS: 'extras'
  },
  HOSPITAL_COVER_LEVEL: [
    {
      label: 'Basic',
      value: ['Basic', 'BasicPlus']
    },
    {
      label: 'Silver',
      value: ['Silver', 'SilverPlus']
    },
    {
      label: 'Bronze',
      value: ['Bronze', 'BronzePlus']
    },
    {
      label: 'Gold',
      value: ['Gold']
    },
    {
      label: 'Not sure',
      value: ''
    }
  ],
  EXCESS: [
    {
      label: '$750',
      value: '750'
    },
    {
      label: '$500',
      value: '500'
    },
    {
      label: '$250',
      value: '250'
    },
    {
      label: '$0',
      value: '0'
    },
    {
      label: 'Not sure',
      value: ''
    }
  ],
  STATE_OPTIONS: [
    {
      label: 'New South Wales',
      value: 'NSW',
      postcodeRanges: [
        {
          from: 1000,
          to: 1999
        },
        {
          from: 2000,
          to: 2599
        },
        {
          from: 2619,
          to: 2899
        },
        {
          from: 2921,
          to: 2999
        }
      ]
    },
    {
      label: 'Victoria',
      value: 'VIC',
      postcodeRanges: [
        {
          from: 3000,
          to: 3999
        },
        {
          from: 8000,
          to: 8999
        }
      ]
    },
    {
      label: 'Queensland',
      value: 'QLD',
      postcodeRanges: [
        {
          from: 4000,
          to: 4999
        },
        {
          from: 9000,
          to: 9999
        }
      ]
    },
    {
      label: 'South Australia',
      value: 'SA',
      postcodeRanges: [
        {
          from: 5000,
          to: 5999
        }
      ]
    },
    {
      label: 'Western Australia',
      value: 'WA',
      postcodeRanges: [
        {
          from: 6000,
          to: 6797
        },
        {
          from: 6800,
          to: 6999
        }
      ]
    },
    {
      label: 'Tasmania',
      value: 'TAS',
      postcodeRanges: [
        {
          from: 7000,
          to: 7999
        }
      ]
    },
    {
      label: 'Northern Territory',
      value: 'NT',
      postcodeRanges: [
        {
          from: 800,
          to: 999
        }
      ]
    },
    {
      label: 'Australian Capital Territory',
      value: 'ACT',
      postcodeRanges: [
        {
          from: 200,
          to: 299
        },
        {
          from: 2600,
          to: 2618
        },
        {
          from: 2900,
          to: 2920
        }
      ]
    }
  ],
  INLINE_EDIT_FIELDS: [
    {
      label: 'Reference #',
      formLabel: 'Reference #',
      value: 'reference'
    },
    {
      label: 'Next payment',
      formLabel: 'Next payment date',
      value: 'next-payment-date'
    },
    {
      label: 'Renewal date',
      formLabel: 'Renewal date',
      value: 'renewal'
    },
    {
      label: 'Comments',
      formLabel: 'Comments',
      value: 'comments'
    }
  ],
  ACCOUNT_TYPE: [
    {
      label: 'Home',
      value: 'Residential',
      name: 'residential'
    },
    {
      label: 'Business',
      value: 'SME',
      name: 'sme'
    }
  ],
  LIFE_INSURANCE_PAYMENT_METHODS: [
    { name: 'Through my Super fund', label: 'Through my Super fund', value: 'Through my Super fund' },
    { name: 'Out of pocket', label: 'Out of pocket', value: 'Out of pocket' },
    { name: 'Combination', label: 'Combination', value: 'Combination' }
  ],
  HOME_LOAN_TYPES: [
    { name: 'Fixed', label: 'Fixed', value: 'Fixed' },
    { name: 'Variable', label: 'Variable', value: 'Variable' },
    { name: 'Split (Combined)', label: 'Split (Combined)', value: 'Split (Combined)' }
  ],
  HOME_LOAN_FIXED_TERM_OPTIONS: [
    { name: '1 Year', label: '1 Year', value: '1 Year' },
    { name: '2 Years', label: '2 Years', value: '2 Years' },
    { name: '3 Years', label: '3 Years', value: '3 Years' },
    { name: '4 Years', label: '4 Years', value: '4 Years' },
    { name: '5 Years', label: '5 Years', value: '5 Years' },
    { name: 'Other', label: 'Other', value: 'Other' }
  ],
  HOME_LOAN_TERM_OPTIONS: [
    { name: '10 year', label: '10 year', value: '10 year' },
    { name: '15 year', label: '15 year', value: '15 year' },
    { name: '20 year', label: '20 year', value: '20 year' },
    { name: '25 year', label: '25 year', value: '25 year' },
    { name: '30 year', label: '30 year', value: '30 year' },
    { name: 'Other', label: 'Other', value: 'Other' }
  ],
  HOME_LOAN_PAYMENT_TYPES: [
    { name: 'Principal and Interest', label: 'Principal and Interest', value: 'Principal and Interest' },
    { name: 'Interest Only', label: 'Interest Only', value: 'Interest Only' }
  ],
  HOME_LOAN_PROPERTY_TYPES: [
    { name: 'Owner Occupier', label: 'Owner Occupier', value: 'Owner Occupier' },
    { name: 'Investment Property', label: 'Investment Property', value: 'Investment Property' }
  ],
  PRODUCT_CATEGORY_OPTIONS: [
    'Health insurance',
    'Life insurance',
    'Electricity',
    'Gas',
    'Home loan',
    'Car insurance',
    'Solar',
    'Personal Loans',
    'Home & content insurance',
    'Internet',
    'Subscription',
    'Superannuation',
    'Pet Insurance',
    'Phone',
    'Other'
  ],
  /*
   * CLUB-919 — the verticals whose "How would you like to add your product?" screen offers
   * more than "Add manually" (bill upload, email forwarding, energy connections). The
   * category step only detours through that screen for these; every other category goes
   * straight to its form. The screen itself gates its upload tiles on the same list, so the
   * routing and the screen cannot drift apart again (CLUB-62 routed every category through
   * it; CLUB-475 trimmed the tiles but not the routing).
   */
  VAULT_UPLOAD_VERTICALS: ['health-insurance', 'energy'],
  VAULT_VERTICAL_ROUTE_MAP: {
    'health-insurance': { name: 'dashboard-vault-product-form-health-insurance-step-step', step: 1 },
    'energy': { name: 'dashboard-vault-product-form-energy-step-step', step: 1 },
    'home-loan': { name: 'dashboard-vault-product-form-home-loan-step-step', step: 1 },
    'life-insurance': { name: 'dashboard-vault-product-form-life-insurance-step-step', step: 1 }
  },
  PRODUCT_CATEGORY_VERTICAL_SLUG_MAP: {
    'Health insurance': 'health-insurance',
    'Life insurance': 'life-insurance',
    'Electricity': 'energy',
    'Gas': 'energy',
    'Home loan': 'home-loan',
    'Car insurance': 'car-insurance',
    'Solar': 'solar',
    'Personal Loans': 'personal-loans',
    'Home & content insurance': 'home-content-insurance',
    'Internet': 'internet',
    'Subscription': 'subscription',
    'Superannuation': 'superannuation',
    'Pet Insurance': 'pet-insurance',
    'Phone': 'phone',
    'Other': 'other'
  },
  DEALALERTSTATUSOPTIONS: {
    PENDING: 1,
    NOTIFIED: 2,
    VIEWED: 3,
    ACTIONED: 4
  },
  APPLE_RELAY_EMAIL_DOMAIN: '@privaterelay.appleid.com',
  ANDROID_APP_STORE_URL : 'https://play.google.com/store/apps/details?id=com.compareclub.app&pli=1',
  IOS_APP_STORE_URL : 'https://apps.apple.com/au/app/compare-club/id6752555566',
  CONFIDENCE_SCORE_TRIGGER_CODE:{
    [VERTICAL_IDS.HEALTH_INSURANCE]:'health_review',
    [VERTICAL_IDS.ENERGY]:'energy_review',
    [VERTICAL_IDS.HOME_LOANS]:'home_loan_review',
    [VERTICAL_IDS.LIFE_INSURANCE]:'life_review'
  },
  CONFIDENCE_SCORE_STATUS_COLORS:{
    STATUS_COLOR_GOOD : 'bg-semantic-states-success-default',
    STATUS_COLOR_WORTH_A_LOOK : 'bg-semantic-vertical-finance-base',
    STATUS_COLOR_TIME_TO_SWITCH : 'bg-semantic-states-error-default',
    STATUS_COLOR_WORTH_A_LOOK_DEFAULT: 'bg-semantic-states-warning-default'
  },
  CONFIDENCE_SCORE_ENABLED_VERTICALS:[VERTICAL_IDS.HEALTH_INSURANCE, VERTICAL_IDS.ENERGY, VERTICAL_IDS.LIFE_INSURANCE, VERTICAL_IDS.HOME_LOANS],
  FILE_PARSED_STATUS: {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed'
  },
  /*
   * Vault `source` values (how a vault product entered the member's vault).
   * EMAIL_FORWARD — created from a bill forwarded to the inbound email address.
   * EMAIL_SCRAPE  — created from a bill found by scanning a connected Gmail inbox.
   *                 TODO(CLUB-480): confirm the value with the backend.
   */
  VAULT_SOURCE: {
    EMAIL_FORWARD: 'email_forward',
    EMAIL_SCRAPE: 'email_scraping',
    CDR_CONSENT: 'cdr_consent'
  },
  /*
   * Forwarded-bill (inbound email) status lifecycle.
   * From the Email-Forward-to-Vault technical spec — the `inbound_emails.status`
   * column returned by GET /forwarded-bills:
   *   received → awaiting_n8n → vault_created | draft_review | draft_failed
   * plus unmatched / bounced for no-account senders.
   *  RECEIVED      – sender matched, before parsing ("Got it")
   *  AWAITING_N8N  – attachment(s) sent to the parse pipeline
   *  VAULT_CREATED – at least one vault created (exact / name_exact match)
   *  DRAFT_REVIEW  – draft vault(s) created, awaiting member confirmation
   *  DRAFT_FAILED  – no usable bill produced from any attachment
   *  UNMATCHED     – no Club+ account for the sender
   *  BOUNCED       – throttled bounce sent to an unmatched sender
   */
  FORWARDED_BILL_STATUS: {
    RECEIVED: 'received',
    AWAITING_N8N: 'awaiting_n8n',
    VAULT_CREATED: 'vault_created',
    DRAFT_REVIEW: 'draft_review',
    DRAFT_FAILED: 'draft_failed',
    UNMATCHED: 'unmatched',
    BOUNCED: 'bounced'
  },
  // email_scraping_status on GET /memberhub/connected-accounts (CLUB-480): where
  // a connected inbox's bill scan stands. bills_found always comes with drafts.
  EMAIL_SCRAPING_STATUS: {
    PROCESSING: 'processing',
    NO_BILLS_FOUND: 'no_bills_found',
    BILLS_FOUND: 'bills_found'
  },
  UPLOADER_WIDGET:{
    FILE_TYPES:{
      FILE:1,
      LINK:2
    }
  },
  HEALTH_QUICK_COMPARE_PATH: '/dashboard/quick-compare/health/',
  ENERGY_QUICK_COMPARE_PATH: '/dashboard/quick-compare/energy/',
  HOME_LOAN_QUICK_COMPARE_PATH: '/dashboard/quick-compare/home-loan/',
  LIFE_QUICK_COMPARE_PATH: '/dashboard/quick-compare/life/',
  TRAVEL_INSURANCE_QUICK_COMPARE_PATH: '/dashboard/quick-compare/travel-insurance/',
  /*
     * Energy CDR (Consent Data Rights) constants
     */
  CDR_USE_CASE_ID: 'ENERGY_COMPARISON',
  /*
     * Dialling code applied to numbers sent to Descope, which requires E.164. Member Hub
     * stores Australian mobiles in local format ("0412345678"), so a stored number cannot
     * be handed to Descope as-is.
     */
  AU_COUNTRY_CODE
}
